import { useEffect, useMemo, useState } from "react"

import { Underline, XIcon } from "lucide-react"
import { ToggleOptionGroup, ToggleOptionGroupItem, DefaultTooltip, DefaultPopover, Separator, List, Select } from "twyne-new.ui"

import { useCraftProp } from "../../utils"
import { UNIT_OPTIONS } from "../../utils/predefined_sizes"
import PaletteColors from "../Color/Palette"
import { ArbitraryUnit } from "../PredefinedArbitraryUnit"
import { ListContent, ListLabel, ListWrapper, ListItem } from "../components/list"

import { Icon } from "."

const Decoration = () => {
  const { value: decoration } = useCraftProp('decoration')
  const { value: textDecoration, setValue: setTextDecoration } = useCraftProp('decoration.value')

  useEffect(() => {
    if (decoration && typeof decoration === 'string') {
      setTextDecoration(decoration)
    }
  }, [decoration, setTextDecoration])

  return <>
    <ToggleOptionGroup size='lg' value={textDecoration} type='single' onValueChange={value => value !== 'advanced' && setTextDecoration(value)} className='w-full'>
      <ToggleOptionGroupItem value='underline'>
        <DefaultTooltip content='Underline' triggerProps={{ className: 'w-full flex justify-center' }}>
          <Icon value='Underline' />
        </DefaultTooltip>
      </ToggleOptionGroupItem>
      <ToggleOptionGroupItem value='overline'>
        <DefaultTooltip content='Overline' triggerProps={{ className: 'w-full flex justify-center' }}>
          <Underline value='Overline' className='rotate-180' size={14} />
        </DefaultTooltip>
      </ToggleOptionGroupItem>
      <ToggleOptionGroupItem value='strike'>
        <DefaultTooltip content='Strike Through' triggerProps={{ className: 'w-full flex justify-center' }}>
          <Icon value='Strikethrough' />
        </DefaultTooltip>
      </ToggleOptionGroupItem>
      <Advanced />
    </ToggleOptionGroup>
  </>
}

const Advanced = () => {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState('general')
  const { value: style } = useCraftProp('decoration.style')
  const { value: underlineOffset } = useCraftProp('decoration.underlineOffset')
  const { value: thickness } = useCraftProp('decoration.thickness')
  const { value: color } = useCraftProp('decoration.color')

  const hasAdvanced = useMemo(() => style || underlineOffset || thickness || color, [style, underlineOffset, thickness, color])

  return <DefaultPopover
    trigger={<ToggleOptionGroupItem value='advanced' className='relative'>
      {hasAdvanced && <div className='absolute top-0 right-0 twy-bg-accent w-[10px] h-[10px] rounded-full'></div>}
      <DefaultTooltip side='left' content={<><div>Advanced</div> <div className='text-sm font-bold mt-1'>{hasAdvanced && 'Has advanced text decoration options'}</div></>} triggerProps={{ className: 'w-full flex justify-center' }}>
        <Icon value='EllipsisVertical' />
      </DefaultTooltip>
    </ToggleOptionGroupItem>}
    triggerProps={{ asChild: true }}
    contentProps={{ className: 'p-0', side: 'bottom', align: 'center' }}
    wrapperProps={{ open, onOpenChange: setOpen }}
  >
    <div className='p-2 pb-0'>
      <div className='flex justify-between items-center'>
        Text Decoration
        <XIcon size='16' className='cursor-pointer' onClick={() => setOpen(false)} />
      </div>
      <Separator className='m-0 my-2' />

      <ToggleOptionGroup className='mb-4' type='single' value={tab} onValueChange={setTab}>
        <ToggleOptionGroupItem value='general'>General</ToggleOptionGroupItem>
        <ToggleOptionGroupItem value='color'>Color</ToggleOptionGroupItem>
      </ToggleOptionGroup>
    </div>

    {tab === 'color' && <List><PaletteColors className='pr-2' name='decoration.color' /></List>}
    {tab === 'general' && <TabGeneral />}
  </DefaultPopover>
}

const TabGeneral = () => {
  const { value: textDecoration } = useCraftProp('decoration.value')
  const { value: style, setValue: setStyle } = useCraftProp('decoration.style')

  return <>
    <ListWrapper>
      <div className='flex flex-col gap-2'>
        {textDecoration === 'underline' && <>
          <ListItem>
            <ListLabel>Offset</ListLabel>
            <ListContent>
              <ArbitraryUnit
                name='decoration.underlineOffset'
                unitOptions={[{ label: 'Unitless', value: 'unitless' }, ...UNIT_OPTIONS]}
              />
            </ListContent>
          </ListItem>
        </>}
        <ListItem>
          <ListLabel>Thickness</ListLabel>
          <ListContent>
            <ArbitraryUnit
              name='decoration.thickness'
              unitOptions={[{ label: 'Unitless', value: 'unitless' }, ...UNIT_OPTIONS]}
            />
          </ListContent>
        </ListItem>
        <ListItem>
          <ListLabel>Style</ListLabel>
          <ListContent>
            <Select clearable value={style} onChange={option => setStyle(option?.value)} options={STYLE_OPTIONS} variant='accent' className='w-full' />
          </ListContent>
        </ListItem>
      </div>
    </ListWrapper>
  </>
}

const STYLE_OPTIONS = [
  { label: 'Solid', value: 'decoration-solid' },
  { label: 'Double', value: 'decoration-double' },
  { label: 'Dotted', value: 'decoration-dotted' },
  { label: 'Dashed', value: 'decoration-dashed' },
  { label: 'Wavy', value: 'decoration-wavy' },
]

export default Decoration
